﻿<template>
    <b-container class="product-list">
        <b-card class="pb-2 border-0">
            <div class="title-underlined pe-5 mb-4 text-start">{{ categoryName }}</div>

            <b-row v-if="showSubCategoriesFilter" class="banner py-3 mb-3">
                <b-col class="d-none d-sm-flex justify-content-center align-items-center" cols="12" md="6" lg="4">
                    <img :src="pageFilter.banner_url"
                         :alt="pageFilter.banner_text" />
                </b-col>
                <b-col class="d-flex align-items-center justify-content-center text-center" cols="12" md="6" lg="8">
                    <div v-html="pageFilter.banner_text"></div>
                </b-col>
            </b-row>

            <template v-if="subCategories && subCategories.length && showSubCategoriesFilter">
                <b-row class="mb-5 mt-3">
                    <b-col class="compact-item p-0" cols="12" md="6" lg="3">
                        <div class="p-3 d-flex flex-column justify-content-center align-items-center h-100 subcategories-layout border-bottom border-2 border-primary">
                            <div class="text-primary product-subcategories">{{ $t('allSubcategories') }}</div>
                        </div>
                    </b-col>
                    <b-col class="compact-item p-0" v-for="(subCategory, index) in subCategories" :key="index" cols="12" md="6" lg="3">
                        <div class="p-3 d-flex flex-column justify-content-center align-items-center h-100 subcategories-layout border-bottom border-2 border-light">
                            <router-link class="text-dark product-subcategories px-xl-4 text-center" :to="`${$route.path}/${subCategory.se_name}`">{{ subCategory.name }} ({{ subCategory.number_of_products }})</router-link>
                        </div>
                    </b-col>
                </b-row>
            </template>

            <product-view-filter :sortOptions="sortOptions"
                                 :pageSizeOptions="pageSize"
                                 :showFilters="pageFilter && pageFilter.specification_filter.attributes && pageFilter.specification_filter.attributes.length > 0"
                                 @change-sort-option="changeSortOption"
                                 @change-view="changeView"
                                 @change-page-size="changePageSize"
                                 @open-mobile-filter="openMobileFilter" />


            <b-container id="list" v-if="isCompactView" class="p-0">
                <b-row no-gutters>
                    <b-col v-for="(product, index) in products"
                           :key="index"
                           cols="12"
                           md="6"
                           xl="3"
                           class="compact-item">
                        <template v-if="!isFuses">
                            <div class="border-0 p-3 rounded-0 h-100 fuse-layout">

                                <div>
                                    <p class="product-name fs-6 mb-4">
                                        <router-link :to="product.product_url">
                                            <b-card class="border-0 mb-2"
                                                    :img-src="product.image_url"
                                                    :img-alt="product.image_alt">
                                            </b-card>

                                            {{ $t('part') }} #: {{ product.sku }}
                                        </router-link>
                                    </p>

                                    <div class="product-description" v-html="product.short_description"></div>

                                    <product-action :productId="product.id"
                                                    :showDatasheet="product.show_datasheet"
                                                    :quantity="quantities[index]"
                                                    :enableDatasheetGeneration="pageFilter.enable_datasheet_generation"
                                                    :datasheet="productDocument(product.documents, DocumentType.DATASHEET)"
                                                    :installation="productDocument(product.documents, DocumentType.INSTALLATION)"
                                                    :isMobileList="isMobile"></product-action>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 py-4 d-flex flex-column h-100 border-layout">
                                <div>
                                    <p class="product-name">
                                        <router-link :to="product.product_url">
                                            <div class="product-name-title" v-html="product.name"></div>
                                            <div class="product-name-part mt-2">{{ $t('part') }} #: {{ product.sku }}</div>
                                        </router-link>
                                    </p>

                                </div>

                                <div class="mt-auto">
                                    <b-form-spin-button class="customer-count mb-4 mt-3" min="1" max="100" step="1" v-model="quantities[index]" />

                                    <product-action :productId="product.id"
                                                    :showDatasheet="product.show_datasheet"
                                                    :enableDatasheetGeneration="pageFilter.enable_datasheet_generation"
                                                    :quantity="quantities[index]"
                                                    :datasheet="productDocument(product.documents, DocumentType.DATASHEET)"
                                                    :isMobileList="isMobile"></product-action>
                                </div>
                            </div>
                        </template>
                    </b-col>
                </b-row>
            </b-container>
            <b-container id="list" v-else>
                <b-row v-for="(product, index) in products"
                       :key="index"
                       :class="[{'py-5 d-flex align-self-center': isFuses}, {'border-top': !index}, 'pt-3 border-bottom pb-4']">
                    <template v-if="!isFuses">
                        <b-col>
                            <router-link :to="product.product_url">
                                <b-card class="border-0"
                                        :img-src="product.image_url"
                                        :img-alt="product.image_alt">
                                </b-card>
                            </router-link>
                        </b-col>
                        <b-col cols="12" md="5">
                            <b-card class="border-0">
                                <p class="product-name fs-6">
                                    <router-link :to="product.product_url">
                                        {{ product.name }}
                                    </router-link>
                                </p>
                                <hr />
                                <p class="fw-bold">{{ $t('part') }} #: {{ product.sku }}</p>
                                <div v-html="product.short_description"></div>
                                <b-form-spin-button class="customer-count" min="1" max="100" step="1"
                                                    v-model="quantities[index]"
                                                    :label-decrement="`${product.name} ArrowDown`"
                                                    :label-increment="`${product.name} ArrowUp`" />
                            </b-card>
                        </b-col>
                        <b-col>
                            <product-action class="pt-3 pt-md-0"
                                            :showDatasheet="product.show_datasheet"
                                            :productId="product.id"
                                            :quantity="quantities[index]"
                                            :enableDatasheetGeneration="pageFilter.enable_datasheet_generation"
                                            :datasheet="productDocument(product.documents, DocumentType.DATASHEET)"
                                            :installation="productDocument(product.documents, DocumentType.INSTALLATION)"></product-action>
                        </b-col>
                    </template>

                    <template v-else>
                        <b-col cols="12" md="4">
                            <div class="product-fuse-header my-2">{{ $t('productName') }}</div>
                            <p class="product-name fs-6">
                                <router-link :to="product.product_url">
                                    {{ product.name }}
                                </router-link>
                            </p>
                        </b-col>
                        <b-col cols="12" md="3">
                            <div class="product-fuse-header my-2">{{ $t('partNumber') }}</div>
                            <p class="fw-bold">{{ $t('part') }} #: {{ product.sku }}</p>
                        </b-col>
                        <b-col cols="12" md="2">
                            <div class="product-fuse-header my-2">Quantity</div>
                            <b-form-spin-button class="customer-count" min="1" max="100" step="1" v-model="quantities[index]" />
                        </b-col>
                        <b-col cols="12" md="3" class="pt-1">
                            <product-action class="pt-4"
                                            :showDatasheet="product.show_datasheet"
                                            :productId="product.id"
                                            :datasheet="productDocument(product.documents, DocumentType.DATASHEET)"
                                            :enableDatasheetGeneration="pageFilter.enable_datasheet_generation"
                                            :quantity="quantities[index]"></product-action>
                        </b-col>
                    </template>
                </b-row>
            </b-container>

            <b-pagination class="pt-5"
                          v-model="currentPage"
                          :total-rows="totalItems"
                          :hide-goto-end-buttons="isMobile"
                          :per-page="perPage"
                          align="center"></b-pagination>

        </b-card>
    </b-container>

    <b-modal v-model="isMobileFilter" title="Filter" centered footer-class="justify-content-center">
        <product-filter @sendSelectedFilters="updateFilters" :pageFilter="pageFilter" />

        <template v-slot:footer>
            <b-button variant="primary" class="btn-trimm-wide" pill @click="isMobileFilter = false">{{ $t('ok') }}</b-button>
        </template>
    </b-modal>
</template>

<script>
    import ProductCard from '@/components/views/category/product-card.vue';
    import { DOCUMENT_TYPE } from '@/constants/products-info';
    import { catalogService } from '@/services';
    import ProductAction from './product-action.vue';
    import ProductViewFilter from './product-view-filter.vue';
    import ProductFilter from '@/components/views/category/product-filter.vue';
    import ProductUtils from "@/utils/product-utils";
    import { MediaPointConstant } from '@/constants/mediaPoint-constants';
    import seoMixin from '@/mixins/seo-mixin';
    import { LanguageName } from '@/enums/language';

    export default {
        components: {
            'product-card': ProductCard,
            'product-action': ProductAction,
            'product-view-filter': ProductViewFilter,
            'product-filter': ProductFilter
        },

        emits: ['update-list'],

        mixins: [seoMixin],

        data() {
            return {
                quantities: [],
                isCompactView: false,
                isMobileFilter: false,

                sortOptions: [],

                currentSortOption: 0,

                isLoading: false,
                currentPage: 1,
                filtersMobile: [],
                subCategories: [],
                totalPages: null,
                totalItems: null,
                perPage: 0
            }
        },

        props: {
            filters: Array,
            catalog: Object,
            pageFilter: Object,
            showSubCategoriesFilter: Boolean
        },

        computed: {
            isMobile() {
                return window.innerWidth <= MediaPointConstant.MediaPointMd;
            },

            products() {
                return this.catalog.products;
            },

            pageSize() {
                return this.pageFilter.page_size_options;
            },

            categoryName() {
                return this.pageFilter.category_name;
            },

            isFuses() {
                return ProductUtils.isFuseProduct(this.$route);
            },

            isPower() {
                return ProductUtils.isPowerProduct(this.$route);
            },

            DocumentType() {
                return DOCUMENT_TYPE;
            }
        },

        watch: {
            filters() {
                this.updateList();
            },

            filtersMobile() {
                this.updateList();
            },

            currentPage() {
                this.updateList();
            },

            catalog() {
                this.initData();
            }
        },

        async created() {
            await this.initData();

            this.setImagesAttributes();
        },

        methods: {
            async initData() {
                //Get subcategories
                this.subCategories = await catalogService
                    .getCatalogSubCategories(parseInt(this.pageFilter.category_id));

                if (window.innerWidth <= 400 && this.isPower)
                    this.isCompactView = true;

                this.totalPages = this.catalog.total_pages;
                this.totalItems = this.catalog.total_items;

                this.sortOptions = this.pageFilter.available_sort_options.map(option => {
                    return {
                        text: option.text,
                        value: option.value
                    };
                });

                this.perPage = this.catalog.page_size;

                this.pushQuantities();
            },

            increase() {
                this.count++;
            },

            decrease() {
                if (this.count !== 1)
                    this.count--;
            },

            changeSortOption(sortOption) {
                this.currentSortOption = parseInt(sortOption.sortOption);
                this.currentPage = 1;
                this.updateList();
            },

            changeView(isCompactView) {
                this.isCompactView = isCompactView;
            },

            changePageSize(pageSize) {
                this.perPage = parseInt(pageSize.pageSize);
                this.currentPage = 1;
                this.updateList();
            },

            productDocument(documents, type) {
                if (documents && documents.length) {
                    var doc = documents.find(document => document.type === type && document.language == this.$i18n.locale);

                    //Default document, document language will be null that means that document is general and doesn`t have a certain language
                    if (!doc)
                        doc = documents.find(document => document.type === type && (!document.language || document.language === LanguageName.EN));

                    return doc ? doc.location_url : '';
                }

                return '';
            },

            updateFilters(filters) {
                this.filtersMobile = filters;
            },

            openMobileFilter() {
                this.isMobileFilter = true;
            },

            updateList() {
                this.$emit('update-list', {
                    specification_option_ids: this.isMobile ? this.filtersMobile : this.filters,
                    page_number: this.currentPage,
                    page_size: this.perPage,
                    order_by: this.currentSortOption
                });

                this.resetQuantities();
            },

            pushQuantities() {
                //Push default values
                for (let i = 0; i < this.perPage; i++)
                    this.quantities.push(1);
            },

            resetQuantities() {
                //Reset if products quantity was changed
                this.quantities = [];

                this.pushQuantities();
            },
        }
    }
</script>